import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, ArrowLeft, Search, FileSpreadsheet, Printer, DollarSign, Package } from 'lucide-react';
import Layout from '../../../components/Layout';
import { inventoryReportService, DeadStockItemDTO } from '../../../services/inventoryReportService';
import { exportToCsv, printReport } from '../../../utils/exportUtils';
import './InventoryReportCommon.css';

const fmt    = (n: number) => new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
const fmtQty = (n: number) => new Intl.NumberFormat().format(n);
const fmtDate = (d: string | null) => d ? new Date(d).toLocaleDateString() : 'Never';

const DeadStockReportPage: React.FC = () => {
  const navigate  = useNavigate();
  const [days,        setDays]        = useState('90');
  const [hasSearched, setHasSearched] = useState(false);
  const [searching,   setSearching]   = useState(false);
  const [items,       setItems]       = useState<DeadStockItemDTO[]>([]);

  const handleSearch = async () => {
    setSearching(true);
    setHasSearched(true);
    try {
      const res = await inventoryReportService.getDeadStock({ days: Number(days) });
      const d: DeadStockItemDTO[] = (res.data as any)?.data ?? res.data ?? [];
      setItems(d);
    } catch { setItems([]); }
    finally { setSearching(false); }
  };

  const handleExcel = () => {
    exportToCsv(
      `dead-stock-${days}days-${new Date().toISOString().split('T')[0]}.csv`,
      ['SKU', 'Product', 'Category', 'Qty', 'Unit Cost', 'Total Value', 'Last Movement', 'Days Since Movement'],
      items.map(i => [i.sku, i.productName, i.categoryName ?? '', i.totalQuantity, i.unitCost, i.totalValue, fmtDate(i.lastMovementDate), i.daysSinceLastMovement]),
    );
  };

  const totalValue = items.reduce((s, i) => s + i.totalValue, 0);
  const totalQty   = items.reduce((s, i) => s + i.totalQuantity, 0);

  return (
    <Layout>
      <div className="irp-page">

        <div className="irp-hero">
          <div>
            <h1 className="irp-hero-title"><Clock size={22} strokeWidth={1.8} /> Dead Stock Report</h1>
            <p className="irp-hero-sub">Products with no movement — identify tied-up capital</p>
          </div>
          <div className="irp-hero-actions">
            {hasSearched && items.length > 0 && (
              <div className="irp-export-group">
                <button className="irp-export-btn irp-export-excel" onClick={handleExcel}><FileSpreadsheet size={13} /> Export Excel</button>
                <button className="irp-export-btn irp-export-pdf" onClick={printReport}><Printer size={13} /> Print / PDF</button>
              </div>
            )}
            <button className="irp-back-btn" onClick={() => navigate('/reports/inventory')}><ArrowLeft size={14} /> Back</button>
          </div>
        </div>

        <div className="ims-card">
          <div className="ims-card-header">
            <span className="ims-card-title"><Search size={14} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />Filter</span>
          </div>
          <div className="ims-card-body" style={{ padding: '1rem 1.25rem' }}>
            <div className="irp-filter-bar">
              <div className="irp-filter-group" style={{ flex: '0 0 auto', minWidth: 180 }}>
                <span className="irp-filter-label">No Movement For (days)</span>
                <select className="ims-form-control" value={days} onChange={e => setDays(e.target.value)}>
                  <option value="30">30 days</option>
                  <option value="60">60 days</option>
                  <option value="90">90 days</option>
                  <option value="180">180 days</option>
                  <option value="365">1 year</option>
                </select>
              </div>
              <button className="ims-btn ims-btn-primary" onClick={handleSearch} disabled={searching} style={{ alignSelf: 'flex-end' }}>
                {searching ? <><span className="irp-spinner" /> Searching…</> : <><Search size={14} /> Search</>}
              </button>
            </div>
          </div>
        </div>

        {!hasSearched && (
          <div className="irp-prompt"><Clock size={48} strokeWidth={1} /><p>Select a threshold and click <strong>Search</strong>.</p></div>
        )}

        {hasSearched && !searching && (
          <>
            <div className="ims-stats-row" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))' }}>
              <div className="ims-stat-card">
                <div className="ims-stat-icon irp-icon-dead"><Package size={20} strokeWidth={1.6} /></div>
                <div className="ims-stat-info">
                  <span className="ims-stat-value">{items.length}</span>
                  <span className="ims-stat-label">Dead Stock Items</span>
                </div>
              </div>
              <div className="ims-stat-card">
                <div className="ims-stat-icon irp-icon-total"><Package size={20} strokeWidth={1.6} /></div>
                <div className="ims-stat-info">
                  <span className="ims-stat-value">{fmtQty(totalQty)}</span>
                  <span className="ims-stat-label">Total Units</span>
                </div>
              </div>
              <div className="ims-stat-card">
                <div className="ims-stat-icon irp-icon-out"><DollarSign size={20} strokeWidth={1.6} /></div>
                <div className="ims-stat-info">
                  <span className="ims-stat-value" style={{ fontSize: '1.05rem' }}>{fmt(totalValue)}</span>
                  <span className="ims-stat-label">Value Tied Up</span>
                </div>
              </div>
            </div>

            {items.length === 0 ? (
              <div className="ims-card"><div className="irp-empty">No dead stock found for the selected threshold.</div></div>
            ) : (
              <div className="ims-card">
                <div className="ims-card-header">
                  <span className="ims-card-title">Dead Stock — no movement for {days}+ days ({items.length} items)</span>
                </div>
                <div className="ims-card-body" style={{ padding: 0, overflowX: 'auto' }}>
                  <table className="irp-table">
                    <thead>
                      <tr>
                        <th>SKU</th>
                        <th>Product</th>
                        <th>Category</th>
                        <th className="num">Qty on Hand</th>
                        <th className="num">Unit Cost</th>
                        <th className="num">Total Value</th>
                        <th>Last Movement</th>
                        <th className="num">Days Idle</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map(item => (
                        <tr key={item.productId}>
                          <td><span className="irp-sku">{item.sku}</span></td>
                          <td style={{ fontWeight: 500 }}>{item.productName}</td>
                          <td>{item.categoryName ?? '—'}</td>
                          <td className="num">{fmtQty(item.totalQuantity)}</td>
                          <td className="num">{fmt(item.unitCost)}</td>
                          <td className="num irp-value">{fmt(item.totalValue)}</td>
                          <td style={{ color: 'var(--ims-text-sub)', fontSize: '0.82rem' }}>
                            {fmtDate(item.lastMovementDate)}
                          </td>
                          <td className="num irp-negative">{fmtQty(item.daysSinceLastMovement)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={3} style={{ fontWeight: 700 }}>TOTAL</td>
                        <td className="num">{fmtQty(totalQty)}</td>
                        <td></td>
                        <td className="num">{fmt(totalValue)}</td>
                        <td colSpan={2}></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default DeadStockReportPage;
